import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box} from '@mui/material';

import {
  DocumentArea,
  RedactionTrigger,
} from '../../../../document-view-prizmdoc/DocumentArea';
import {
  DocumentPathType,
  usePrizmDocAnnotations,
} from '../../../../document-view-prizmdoc/hooks/usePrizmDoc';
import './DocumentViewerPanel.scss';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {
  DocViewKeyword,
  LoadViewerResponse,
} from '../../../../../api/types/document.type';
import {ViewerControlTextSelectedEvent} from '../../../../../shared/interfaces';
import {ISelectItem} from '../../../../../shared/types';
import SingleRedactionPopup from '../redaction-menu/SingleRedactionPopup';
import {ToastType} from '../../../../../shared/constants';
import {t} from 'i18next';
import ArrToastNotification, {
  ArrStatusToastProps,
} from '../../../../common/arr-toast-notification/ArrToastNotification';
import ReactDOM from 'react-dom';
import {
  assignColorsToKeywords,
  filterAccusoftSearchTerm,
  remarksColors,
} from '../../../docViewerUtils';
import {ToolbarActionType} from '../../../doc-navigation/main-panel-toolbar/MainPanelToolbar';

export interface DocumentViewerPanelProps {
  setViewerControl: React.Dispatch<React.SetStateAction<ViewerControl | null>>;
  setViewerReady: React.Dispatch<React.SetStateAction<boolean>>;
  setInitialScaleFactor: React.Dispatch<React.SetStateAction<number | null>>;
  setIsReady: React.Dispatch<React.SetStateAction<boolean>>;
  docData?: LoadViewerResponse;
  duplicateDocs: string;
  setPageData: React.Dispatch<
    React.SetStateAction<{
      totalPages: number;
      pageNumber: number;
    }>
  >;
  isLoading: boolean;
  setTextSelection?: React.Dispatch<
    React.SetStateAction<ViewerControlTextSelectedEvent>
  >;
  selectionMode: string;
  selectedToolbarOption?: string;
  isTranslations?: boolean;
  isImageTab?: boolean;
  conceptResults?: {jsonSentenceInfo: string}[];
  setSearchFn?: React.Dispatch<
    React.SetStateAction<
      | ((
          vc: ViewerControl,
          searchTerm: string,
          isPersistentSearch: boolean
        ) => void)
      | null
    >
  >;
  setManualSearchResults?: React.Dispatch<React.SetStateAction<SearchResult[]>>;
  setSearchLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  showWarning?: boolean;
}

const DocumentViewerPanel: React.FC<DocumentViewerPanelProps> = ({
  docData,
  setViewerControl,
  setViewerReady,
  duplicateDocs = '',
  setInitialScaleFactor,
  setIsReady,
  setPageData,
  setTextSelection,
  selectionMode,
  selectedToolbarOption,
  isTranslations = false,
  isImageTab = false,
  setSearchFn,
  setManualSearchResults,
  setSearchLoading,
  showWarning,
  conceptResults,
}) => {
  const [selectedValue, setSelectedValue] = useState<ISelectItem | null>(null);
  const {
    user: {userId},
  } = useSelector((state: RootState) => state.authenticatedUser);
  const keywordsHighlilght = useSelector(
    (state: RootState) => state.docView.docViewConfiguration?.keywords
  );
  const activeDocument = useSelector(
    (state: RootState) => state.docView.activeDoc
  );

  const highlightTerms = useMemo(() => {
    if (activeDocument) {
      const {ActualParameters} = activeDocument;
      const terms = filterAccusoftSearchTerm(ActualParameters ?? '');
      const assignedColors = assignColorsToKeywords(
        keywordsHighlilght?.map((keyword) => keyword.colorCode ?? '') ?? [],
        [
          ...terms,
          ...(conceptResults?.map(
            (concept, index) => `CONCEPT_SEARCH_COLOR_${index}`
          ) ?? []),
        ]
      );

      const emptyConcepts: {description: string; color: string}[] = [];
      const newArray: DocViewKeyword[] = terms.map((term, index) => ({
        keywordID: index,
        keywordName: term,
        colorCode: assignedColors[term],
        keywords: term,
      }));
      if (conceptResults?.length) {
        conceptResults.forEach((conceptResult, index) => {
          const concepts = JSON.parse(conceptResult?.jsonSentenceInfo ?? '[]');
          const description =
            activeDocument?.ConceptParameters?.split('``')[index] ?? '';
          const colorCode = assignedColors[
            `CONCEPT_SEARCH_COLOR_${index}`
          ].replace('#', '');
          let sentences = '';
          //logic taken from sightline
          for (let iIndex = concepts.length - 1; iIndex >= 0; iIndex--) {
            sentences +=
              concepts[iIndex].text
                .replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
                .replace(/\s+/g, '\\s+') + '|';
          }
          if (sentences.length > 0) {
            sentences = sentences.slice(0, -1); //remove last char
            newArray.push({
              keywordID: 100 + newArray.length,
              keywordName: sentences,
              description: description,
              colorCode: colorCode,
              keywords: sentences,
              isConcept: true,
            });
          } else if (description) {
            emptyConcepts.push({
              description: description,
              color: colorCode,
            });
          }
        });
      }
      return {highlights: newArray, emptyConcepts: emptyConcepts};
    }
    return {highlights: [], emptyConcepts: []};
  }, [activeDocument, conceptResults]);

  const viewerRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement>(null);

  const annotationLayerId = useSelector(
    (state: RootState) => state.docView.docViewConfiguration.annotationLayerId
  );

  const [docFilePath, setDocFilePath] = useState<DocumentPathType | null>(null);
  const [connectionId] = useState('36096a6d~3c60~4bac~aa41~94524d3a0c2f'); // Update later with actual SignalR connectionID
  const {reductionPropagation: redactionPropagationEnabled} = useSelector(
    (state: RootState) => state.docView
  );

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState(ToastType.Success);
  const [redactionTriggerState, setRedactionTriggerState] =
    useState<RedactionTrigger>({trigger: false});

  const [currentRemarkColor, setCurrentRemarkColor] = useState<string>(
    remarksColors[0]
  );

  const triggerToast = useCallback(
    (
      toastType: ToastType | ((prevState: ToastType) => ToastType),
      toastMessage: string
    ) => {
      setToastMessage(toastMessage);
      setToastStatus(toastType);
      setShowToast(true);
    },
    []
  );
  const handleRedactionSaved = useCallback(() => {
    setRedactionTriggerState({trigger: true});
  }, []);
  const [redactionMarkId, setRedactionMarkId] = useState<{
    id: string;
    redactionId: string;
    mark: Mark;
  } | null>(null);
  const {
    viewerControl,
    thumbnailControl,
    selectedMark,
    setSelectedMark,
    initialScaleFactor,
    isViewerReady,
    totalPages,
    pageNumber,
    textSelection,
    markupLoaded,
    highlightSearchResults,
    markForRemark,
    setMarkForRemark,
    searchDocument,
    manualSearchResults,
    searchLoading,
  } = usePrizmDocAnnotations(
    docFilePath,
    viewerRef,
    thumbRef,
    true,
    setViewerReady,
    duplicateDocs?.split(';'),
    annotationLayerId,
    connectionId,
    rightPanelRef,
    setRedactionMarkId,
    handleRedactionSaved,
    selectionMode,
    redactionPropagationEnabled,
    triggerToast,
    [...(keywordsHighlilght ?? []), ...highlightTerms.highlights],
    currentRemarkColor,
    isTranslations,
    isImageTab
  );

  useEffect(() => {
    setSearchLoading?.(searchLoading);
  }, [searchLoading, setSearchLoading]);

  useEffect(() => {
    setManualSearchResults?.(manualSearchResults);
  }, [manualSearchResults, setManualSearchResults]);

  useEffect(() => {
    setSearchFn?.(() => {
      return searchDocument;
    });
  }, [searchDocument, setSearchFn]);

  useEffect(() => {
    setViewerReady(isViewerReady);
  }, [isViewerReady, setViewerReady]);

  useEffect(() => {
    setPageData({totalPages, pageNumber});
  }, [setPageData, totalPages, pageNumber]);

  useEffect(() => {
    setViewerControl(viewerControl);
  }, [setViewerControl, viewerControl]);

  useEffect(() => {
    setInitialScaleFactor(initialScaleFactor);
  }, [setInitialScaleFactor, initialScaleFactor]);

  useEffect(() => {
    setIsReady(isViewerReady);
  }, [setIsReady, isViewerReady]);

  useEffect(() => {
    if (setTextSelection) {
      setTextSelection(textSelection);
    }
  }, [setTextSelection, textSelection]);

  useEffect(() => {
    if (selectedToolbarOption !== ToolbarActionType.Remark && markForRemark) {
      // delete existing mark selection and unsaved marks if closing Remarks Mode
      setMarkForRemark(null);
      if (viewerControl) {
        viewerControl.deselectAllMarks();
        viewerControl.deleteMarks([markForRemark]);
      }
    }
  }, [selectedToolbarOption, markForRemark, setMarkForRemark, viewerControl]);

  useEffect(() => {
    if (docData) {
      const paths = docData.documentFilePaths ?? [];
      setDocFilePath({
        documentId: docData.docID,
        documentExt: docData.documentExtension,
        documentPath: docData.documentPath,
        ...(paths.length > 1 ? {documentFilePaths: paths} : {}),
      });
    }
  }, [docData, setDocFilePath]);

  const closeSingeRedactionPopup = () => {
    setRedactionMarkId(null);
    setSelectedValue(null);

    if (viewerControl && viewerControl?.deleteMarks && redactionMarkId?.mark) {
      viewerControl.deleteMarks([redactionMarkId.mark]);
    }
  };

  const saveSingleRedaction = () => {
    if (viewerControl) {
      const mark = redactionMarkId?.mark;

      if (mark) {
        mark.setData(
          'RedactionTagID',
          selectedValue?.id ? `${selectedValue?.id}` : ''
        );
        annotationLayerId &&
          viewerControl
            .saveMarkup(
              `${docData?.docID}-${redactionMarkId?.redactionId}-${userId}-${redactionPropagationEnabled}-${selectedValue?.id}-${annotationLayerId}-${duplicateDocs?.split(';').join(',')}${connectionId ? `-${connectionId}` : ''}-N`
            )
            .then(() => {
              setRedactionTriggerState({trigger: true});
              triggerToast(
                ToastType.Success,
                t('docView.redationMenu.redactionSuccess')
              );
            });
      }
    }
    setRedactionMarkId(null);
    setSelectedValue(null);
  };

  const SuccessToast = useCallback((props: ArrStatusToastProps) => {
    return ReactDOM.createPortal(
      <ArrToastNotification {...props} />,
      document.body
    );
  }, []);

  return (
    <Box ref={containerRef} className="document-viewer-panel-wrapper">
      {showToast && (
        <SuccessToast
          message={toastMessage}
          onClose={() => {
            setShowToast(false);
            setToastMessage('');
          }}
          status={[toastStatus]}
        />
      )}
      {redactionMarkId && (
        <SingleRedactionPopup
          closeSingeRedactionPopup={closeSingeRedactionPopup}
          saveSingleRedaction={saveSingleRedaction}
          selectedValue={selectedValue}
          setSelectedValue={setSelectedValue}
        />
      )}
      <DocumentArea
        thumbRef={thumbRef}
        rightPanelRef={rightPanelRef}
        viewerRef={viewerRef}
        containerRef={containerRef}
        viewerControl={viewerControl}
        thumbnailControl={thumbnailControl}
        selectedMark={selectedMark}
        documentId={docData?.docID}
        markupLoaded={markupLoaded}
        selectedToolbarOption={selectedToolbarOption}
        setSelectedMark={setSelectedMark}
        setRemark={setMarkForRemark}
        redactionTriggerState={redactionTriggerState}
        highlightSearchResults={highlightSearchResults}
        markForRemark={markForRemark}
        setCurrentRemarkColor={setCurrentRemarkColor}
        duplicateDocs={duplicateDocs?.split(';')}
        annotationLayerId={annotationLayerId}
        connectionId={connectionId}
        showWarning={showWarning}
        emptyConcepts={highlightTerms.emptyConcepts}
        searchLoading={searchLoading}
      />
    </Box>
  );
};

export default DocumentViewerPanel;
